import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Search, MapPin, Phone, Globe, Clock, Plus, 
  ThumbsUp, ThumbsDown, Heart, Building2, Stethoscope, 
  Users, Scale, Dumbbell, BookOpen, Coffee, Wine, 
  ExternalLink, Filter
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  bar: Wine,
  club: Wine,
  restaurant: Coffee,
  cafe: Coffee,
  clinic: Stethoscope,
  testing_center: Stethoscope,
  support_group: Users,
  community_center: Building2,
  shelter: Building2,
  legal_aid: Scale,
  therapist: Heart,
  gym: Dumbbell,
  bookstore: BookOpen,
  other: MapPin
};

const categoryLabels = {
  bar: 'Bar',
  club: 'Club',
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  clinic: 'Clinic',
  testing_center: 'Testing Center',
  support_group: 'Support Group',
  community_center: 'Community Center',
  shelter: 'Shelter',
  legal_aid: 'Legal Aid',
  therapist: 'Therapist',
  gym: 'Gym',
  bookstore: 'Bookstore',
  other: 'Other'
};

const Resources = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchCity, setSearchCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddResource, setShowAddResource] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // New resource form
  const [newResource, setNewResource] = useState({
    name: '',
    category: '',
    description: '',
    address: '',
    city: '',
    state: '',
    phone: '',
    website: '',
    hours: '',
    lgbtq_focused: true,
    tags: []
  });

  const fetchResources = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchCity) params.append('city', searchCity);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await axios.get(`${API}/resources?${params.toString()}`);
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  }, [searchCity, selectedCategory]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/resources/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories');
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchResources();
  };

  const handleVote = async (resourceId, upvote) => {
    try {
      await axios.post(`${API}/resources/${resourceId}/vote?upvote=${upvote}`);
      fetchResources();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleSubmitResource = async () => {
    if (!newResource.name || !newResource.category || !newResource.city) {
      toast.error('Name, category, and city are required');
      return;
    }
    
    try {
      await axios.post(`${API}/resources`, newResource);
      toast.success('Resource submitted for review!');
      setShowAddResource(false);
      setNewResource({
        name: '',
        category: '',
        description: '',
        address: '',
        city: '',
        state: '',
        phone: '',
        website: '',
        hours: '',
        lgbtq_focused: true,
        tags: []
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit resource');
    }
  };

  const getCategoryIcon = (category) => {
    const Icon = categoryIcons[category] || MapPin;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">LGBTQ+ Resources</h1>
              <p className="text-xs text-[#A1A1AA]">Find friendly places near you</p>
            </div>
          </div>
          <Button
            data-testid="add-resource-btn"
            size="sm"
            onClick={() => setShowAddResource(true)}
            className="bg-[#FF2E63] hover:bg-[#E01E50]"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="px-4 py-4 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              data-testid="city-search"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white"
              placeholder="Search by city..."
            />
          </div>
          <Button type="submit" className="bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black">
            Search
          </Button>
        </form>
        
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <Button
            size="sm"
            variant={selectedCategory === '' ? 'default' : 'ghost'}
            onClick={() => setSelectedCategory('')}
            className={selectedCategory === '' ? 'bg-[#FF2E63]' : 'text-[#A1A1AA]'}
          >
            All
          </Button>
          {categories.slice(0, 8).map(cat => (
            <Button
              key={cat}
              size="sm"
              variant={selectedCategory === cat ? 'default' : 'ghost'}
              onClick={() => setSelectedCategory(cat)}
              className={`whitespace-nowrap ${selectedCategory === cat ? 'bg-[#FF2E63]' : 'text-[#A1A1AA]'}`}
            >
              {categoryLabels[cat] || cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Resources List */}
      <div className="px-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">No resources found</h3>
            <p className="text-sm text-[#A1A1AA] mb-4">
              {searchCity ? `No resources in "${searchCity}" yet` : 'Be the first to add a resource!'}
            </p>
            <Button onClick={() => setShowAddResource(true)} className="bg-[#FF2E63] hover:bg-[#E01E50]">
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </Button>
          </div>
        ) : (
          resources.map(resource => {
            const CategoryIcon = getCategoryIcon(resource.category);
            return (
              <div key={resource.id} className="glass rounded-xl p-4" data-testid={`resource-${resource.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    resource.lgbtq_focused ? 'bg-[#FF2E63]/20' : 'bg-white/10'
                  }`}>
                    <CategoryIcon className={`w-6 h-6 ${resource.lgbtq_focused ? 'text-[#FF2E63]' : 'text-[#A1A1AA]'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold truncate">{resource.name}</h3>
                      {resource.lgbtq_focused && (
                        <span className="px-2 py-0.5 bg-[#FF2E63]/20 text-[#FF2E63] text-xs rounded-full">
                          LGBTQ+
                        </span>
                      )}
                    </div>
                    
                    <p className="text-xs text-[#08D9D6] mb-2">{categoryLabels[resource.category] || resource.category}</p>
                    
                    {resource.description && (
                      <p className="text-sm text-[#A1A1AA] mb-2 line-clamp-2">{resource.description}</p>
                    )}
                    
                    <div className="space-y-1 text-sm text-[#A1A1AA]">
                      {resource.address && (
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{resource.address}, {resource.city}</span>
                        </p>
                      )}
                      {!resource.address && resource.city && (
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{resource.city}{resource.state && `, ${resource.state}`}</span>
                        </p>
                      )}
                      {resource.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          <a href={`tel:${resource.phone}`} className="text-[#08D9D6]">{resource.phone}</a>
                        </p>
                      )}
                      {resource.hours && (
                        <p className="flex items-center gap-2">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{resource.hours}</span>
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleVote(resource.id, true)}
                          className="flex items-center gap-1 text-[#A1A1AA] hover:text-[#22c55e] transition-colors"
                        >
                          <ThumbsUp className="w-4 h-4" />
                          <span className="text-sm">{resource.upvotes || 0}</span>
                        </button>
                        <button
                          onClick={() => handleVote(resource.id, false)}
                          className="flex items-center gap-1 text-[#A1A1AA] hover:text-red-500 transition-colors"
                        >
                          <ThumbsDown className="w-4 h-4" />
                          <span className="text-sm">{resource.downvotes || 0}</span>
                        </button>
                      </div>
                      
                      {resource.website && (
                        <a
                          href={resource.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[#08D9D6] text-sm hover:underline"
                        >
                          <Globe className="w-4 h-4" />
                          Website
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Resource Modal */}
      <Dialog open={showAddResource} onOpenChange={setShowAddResource}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit a Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Name *</Label>
              <Input
                data-testid="resource-name"
                value={newResource.name}
                onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Business or organization name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Category *</Label>
                <Select
                  value={newResource.category}
                  onValueChange={(v) => setNewResource(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="resource-category" className="bg-white/5 border-white/10">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">City *</Label>
                <Input
                  data-testid="resource-city"
                  value={newResource.city}
                  onChange={(e) => setNewResource(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="City"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Description</Label>
              <Textarea
                value={newResource.description}
                onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                className="bg-white/5 border-white/10 text-white min-h-[80px]"
                placeholder="Brief description..."
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Address</Label>
              <Input
                value={newResource.address}
                onChange={(e) => setNewResource(prev => ({ ...prev, address: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Street address"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Phone</Label>
                <Input
                  value={newResource.phone}
                  onChange={(e) => setNewResource(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="+1 234 567 8900"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Website</Label>
                <Input
                  value={newResource.website}
                  onChange={(e) => setNewResource(prev => ({ ...prev, website: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="https://..."
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#EDEDED]">Hours</Label>
              <Input
                value={newResource.hours}
                onChange={(e) => setNewResource(prev => ({ ...prev, hours: e.target.value }))}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Mon-Fri 9am-5pm"
              />
            </div>
            
            <p className="text-xs text-[#A1A1AA]">
              Resources are reviewed by admins before being published.
            </p>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowAddResource(false)}
              className="text-[#A1A1AA] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              data-testid="submit-resource-btn"
              onClick={handleSubmitResource}
              className="bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default Resources;
